import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence, useReducedMotion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { GlassPill, Dropdown, Button, EmptyState, Segmented, type DropdownOption } from '@/shared/ui'
import { useBotStore, type BotTab } from './botStore'
import { useBotConversations, useBotConversation, useMonthUsage, usePatchSettings, usePatchConversation } from './hooks/useBot'
import { isTerminal, formatEur } from './botUtils'
import { BotComposer } from './BotComposer'
import { TurnView } from './TurnView'
import { CHROME_SPRING } from './botMotion'
import MemoryTab from './MemoryTab'
import SettingsTab from './SettingsTab'
import ReportTab from './ReportTab'

export function BotPanel() {
  const { t } = useTranslation('bot')
  const reduced = useReducedMotion()

  const isOpen = useBotStore((s) => s.isOpen)
  const viewMode = useBotStore((s) => s.viewMode)
  const closePanel = useBotStore((s) => s.close)
  const activeConversationId = useBotStore((s) => s.activeConversationId)
  const setConversation = useBotStore((s) => s.setConversation)
  const catchUpSeen = useBotStore((s) => s.catchUpSeen)
  const selectedModels = useBotStore((s) => s.selectedModels)
  const activeTab = useBotStore((s) => s.activeTab)
  const setActiveTab = useBotStore((s) => s.setActiveTab)
  const setViewMode = useBotStore((s) => s.setViewMode)
  const draft = useBotStore((s) => s.draft)
  const setDraft = useBotStore((s) => s.setDraft)
  // Header dock toggle (owner feedback round 3 §1): the ONE persisted
  // compare-layout control (Settings' old Segmented is gone). Reuses the
  // same settings-patch mutation SettingsTab used to — never a hand-rolled
  // fetch — so the invalidation-on-success behavior stays in one place.
  const patchSettings = usePatchSettings()

  // BotWidget keeps both shells mounted at all times (so a live viewMode
  // swap plays each shell's own enter/exit instead of an instant
  // unmount+remount) — `shellVisible` is "this shell is the one actually on
  // screen", distinct from the store's `isOpen` ("the widget is open at
  // all, in whichever shell"). Data-fetch `enabled` flags below stay on
  // bare `isOpen` (shared query keys with BotCompareDock — react-query
  // dedupes), but anything about THIS shell's own visible chrome
  // (AnimatePresence, Escape, focus) must key off `shellVisible` so the
  // hidden shell doesn't fight the visible one for Escape/focus.
  const shellVisible = isOpen && viewMode !== 'dock'

  // Gated on isOpen (like useBotConversation just below) — BotPanel is
  // always mounted, so without this these would fetch for every
  // authenticated user with 'bot' read access on app load, whether or not
  // they ever open the widget.
  const { data: conversations } = useBotConversations({ enabled: isOpen })
  const patchConversation = usePatchConversation()
  const { data: conversation } = useBotConversation(activeConversationId)
  const { data: monthUsage, isError: usageError } = useMonthUsage({ enabled: isOpen })

  const panelRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)
  const wasOpenRef = useRef(false)

  // Escape closes the panel — it never cancels in-flight work (that keeps
  // running via botStore.pendingTurnIds regardless of panel visibility).
  // Gated on `shellVisible` (not bare `isOpen`) since both shells stay
  // mounted — otherwise this listener and BotCompareDock's would both be
  // armed whenever the widget is open, regardless of which is showing.
  useEffect(() => {
    if (!shellVisible) return
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      // Bail when focus is inside some OTHER subtree (e.g. the conversation
      // Dropdown's portaled search/menu, which lives outside this subtree)
      // — otherwise this handler and the Dropdown's own Escape listener both
      // fire on the same keypress, and Escape closes the whole panel instead
      // of just dismissing the menu. `document.body` (and `null`) count as
      // "inside": when the Dropdown's portaled menu unmounts on its own
      // Escape handling, the browser drops focus to `body` for an instant,
      // and treating that as "outside" would leave this Escape handler
      // permanently dead until the user manually refocuses something inside
      // the panel.
      const active = document.activeElement
      if (panelRef.current && active && active !== document.body && !panelRef.current.contains(active)) return
      closePanel()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [shellVisible, closePanel])

  // Focus moves into the panel on open, and back to the launcher (or
  // whatever else had focus before) on close.
  //
  // The launcher button unmounts while the panel is open (BotLauncher.tsx)
  // and a fresh DOM node mounts in its place when it closes — a ref
  // captured before open would point at the (by then detached) old node,
  // so `.focus()` on it is a no-op. Look the new node up by its stable id
  // instead, falling back to the captured ref for the rare case where focus
  // came from somewhere other than the launcher.
  useEffect(() => {
    if (shellVisible) {
      previouslyFocusedRef.current = document.activeElement as HTMLElement | null
      panelRef.current?.focus()
    } else if (!isOpen && wasOpenRef.current) {
      // Only restore focus on an actual open->closed transition — never on
      // initial mount (isOpen starts false; nothing should steal focus from
      // wherever the page load left it) and never on a live popover->dock
      // swap, where isOpen stays true and BotCompareDock's own focus effect
      // takes over instead.
      const launcherButton = document.getElementById('bot-launcher-button')
      if (launcherButton) launcherButton.focus()
      else previouslyFocusedRef.current?.focus()
    }
    wasOpenRef.current = isOpen
  }, [shellVisible, isOpen])

  // Opening the panel (or the open conversation gaining new answers while
  // it's open) marks every visible answer seen and decrements the unread
  // badge by exactly however many of them were still unseen — NOT a blanket
  // clearUnseen(), which would also zero out genuinely-unseen answers
  // sitting in other conversations the user hasn't looked at.
  useEffect(() => {
    // activeTab !== 'chat' guard (Contract A fix): Memory/Settings can be the
    // showing tab while the shell stays open on this conversation — an
    // answer finishing there is not "seen" by the user, so it must not be
    // caught up here either (mirrors TurnWatcher's own `viewing` check).
    if (!isOpen || !conversation || activeTab !== 'chat') return
    // Only terminal answers were ever eligible to have bumped unseenCount in
    // the first place (TurnWatcher only bumps once anyNonTerminal is false)
    // — catching up a still-queued/running answer here would steal count
    // from a genuinely unseen finished answer sitting in another
    // conversation, and would also mark it "seen" before the user has
    // actually seen its finished content.
    const visibleAnswerIds = conversation.turns.flatMap((turn) =>
      turn.answers.filter((a) => isTerminal(a.status)).map((a) => a.answerId),
    )
    if (visibleAnswerIds.length > 0) catchUpSeen(visibleAnswerIds)
  }, [isOpen, conversation, activeTab, catchUpSeen])

  // O5/O6: "the user asks with 2–3 models -> dock" is driven directly off
  // the ask mutation's response (useAskTurn in hooks/useBot.ts, which calls
  // setViewMode('dock') as soon as POST /turns resolves ≥2 models) rather
  // than watched here — a turn-count diff on the polled conversation can't
  // tell "a turn just landed" apart from "the user switched to/loaded a
  // conversation that already has multi-model history", and would either
  // misfire on browsing or (if guarded against that) miss a brand-new
  // conversation's very first turn entirely.

  // Scroll the thread to the bottom whenever a new turn lands, or this shell
  // (re)becomes the visible one (e.g. switching back from the dock).
  useEffect(() => {
    const el = bodyRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [shellVisible, conversation?.turns.length])

  // No dedicated "untitled" locale key exists — fall back to the updated
  // date rather than inventing an unlisted i18n key.
  const conversationOptions: DropdownOption[] = (conversations ?? []).map((c) => ({
    value: String(c.id),
    label: c.title ?? new Date(c.updatedAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
    note: c.updatedAt ? new Date(c.updatedAt).toLocaleDateString('de-DE') : undefined,
  }))

  // panel.monthUsage interpolates {{mine}}/{{cap}} (the P1 locale author's
  // var name for the number), but Design Spec §13.4 ("a month-to-date org
  // total is visible to everyone in the header") and cost.js's own comment
  // on monthSpendMicroEur both pin this to the ORG-WIDE total, not the
  // caller's own spend — MonthUsage.mine is a different field entirely. The
  // {{mine}} slot gets the org total regardless of its name.
  const spentEur = (monthUsage?.monthSpendMicroEur ?? 0) / 1_000_000
  const capEur = monthUsage?.capEur ?? 0
  const usageLabel = t('panel.monthUsage', { mine: formatEur(spentEur, 2), cap: formatEur(capEur, 0) })

  // Contract E: "Vergleich" toggle visible when the active (latest) turn
  // already has ≥2 answers, or the composer's current selection has ≥2
  // models queued up for the next ask.
  const activeTurn = conversation?.turns[conversation.turns.length - 1]
  const canCompare = (activeTurn?.answers.length ?? 0) >= 2 || selectedModels.length >= 2

  // Live switch is instant; the PATCH that persists it as the device's
  // default is best-effort and fire-and-forget — a failed PATCH neither
  // reverts the live view nor blocks the UI (worst case: the old default
  // shows again on the next device/reload). `isPending`-gated so a rapid
  // double click can't fire two overlapping PATCHes.
  function handleOpenDock() {
    if (patchSettings.isPending) return
    setViewMode('dock')
    patchSettings.mutate({ compareLayout: 'dock' })
  }

  const examples = (() => {
    const raw = t('empty.examples', { returnObjects: true })
    return Array.isArray(raw) ? (raw as string[]) : []
  })()

  return createPortal(
    <AnimatePresence>
      {shellVisible && (
        <motion.div
          ref={panelRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="false"
          aria-labelledby="bot-panel-title"
          className="fixed z-[9997] bottom-3 inset-x-3 sm:inset-x-auto sm:bottom-24 sm:right-6 w-auto sm:w-[520px] max-w-[calc(100vw-1.5rem)] h-[min(720px,85vh)] outline-none"
          initial={reduced ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0, transition: reduced ? { duration: 0 } : CHROME_SPRING }}
          // Exit is instant (duration: 0), not the entrance spring — mirrors
          // BotLauncher's own instant exit (BotLauncher.tsx). Both this panel
          // and the reappearing launcher anchor at the same bottom-right
          // corner; if this exit animated over SPRING's duration, the
          // launcher's (non-instant) entrance would float over it, on top of
          // it (z-[9998] vs 9997), for however long the spring takes to
          // settle — landing a click meant for the panel/composer on the
          // stale launcher instead. An instant exit means only one of the
          // two is ever present at that corner at a time.
          exit={{ opacity: 0, y: reduced ? 0 : 12, transition: { duration: 0 } }}
        >
          {/* Solid shell (owner 2026-09-01: "make the background opaque") — the
              glass panel let page numbers bleed through the header rows. Same
              treatment the dock got in #433 and the composer in #445: the
              whole popover is a data surface, so it goes flat-opaque
              (DESIGN §14 data-surface pair) with the GlassCard's 28px radius
              and a cast shadow kept for elevation. */}
          <div className="rounded-[28px] bg-surface-container-lowest border border-outline-variant shadow-2xl flex flex-col h-full overflow-hidden">
            {/* Header — glass chrome, two rows. Row 1 (title/Dropdown/Neu/
                month chip/close) is a single non-wrapping row so the close
                button always has a guaranteed, reachable position — the
                Dropdown (min-w-0 flex-1) absorbs the squeeze, "Neu" drops to
                icon-only and the month chip disappears entirely once the
                popover gets too narrow for all of them. Row 2 (tabs/compare)
                sits independently below — a single 7-element row here
                previously let the P3 tabs push × into a second, unreachable
                line. */}
            <div className="flex flex-col gap-2 p-3">
              {/* Restrained cascade reveal on shell open — 40ms stagger per
                  row/body/composer, spring physics via CHROME_SPRING
                  (DESIGN.md §14 "cascade reveals on mount"). Each motion.div
                  only replays its `initial`->`animate` on a fresh mount of
                  this shell (AnimatePresence unmounts the whole tree on
                  close), never on ordinary re-renders while open. */}
              <motion.div
                className="flex items-center gap-2"
                initial={reduced ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0, transition: reduced ? { duration: 0 } : CHROME_SPRING }}
              >
                <h2 id="bot-panel-title" className="font-headline font-bold text-on-surface text-sm mr-1 flex-shrink-0">
                  {t('panel.title')}
                </h2>
                <div className="min-w-0 flex-1">
                  <Dropdown
                    value={activeConversationId != null ? String(activeConversationId) : ''}
                    options={conversationOptions}
                    onChange={(v) => setConversation(v ? Number(v) : null)}
                    // activeConversationId===null means two different things:
                    // genuinely zero conversations exist, or "Neu" was just
                    // clicked while conversations DO exist (an intentional
                    // unsaved-conversation state). Picking the placeholder off
                    // whether the list itself is empty — not off the null
                    // selection — keeps "Neu" from claiming there are no
                    // conversations when conversationOptions still lists them.
                    placeholder={conversationOptions.length > 0 ? t('panel.newConversationPlaceholder') : t('panel.noConversations')}
                    size="compact"
                    variant="glass"
                    usePortal
                    menuZIndex={9998}
                    searchable
                  />
                </div>
                <Button variant="ghost" size="sm" icon="add" aria-label={t('panel.newConversation')} onClick={() => setConversation(null)}>
                  <span className="hidden sm:inline">{t('panel.newConversation')}</span>
                </Button>
                {/* "Löschen" = archive (owner ask 2026-09-01): the row leaves
                    the picker, but turns/usage stay — the cost ledger in the
                    Bericht must never lose rows. No confirm dialog: the
                    action is server-side reversible (archived flag). */}
                {activeConversationId != null && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon="delete"
                    aria-label={t('panel.deleteConversation')}
                    title={t('panel.deleteConversation')}
                    disabled={patchConversation.isPending}
                    onClick={() =>
                      patchConversation.mutate(
                        { id: activeConversationId, body: { archived: true } },
                        { onSuccess: () => setConversation(null) },
                      )
                    }
                  />
                )}
                {/* Hidden while GET /api/bot/usage/month has failed or
                    hasn't resolved yet — an unpriced "0,00 € / 0 €" chip
                    would misreport spend as zero rather than unknown. Also
                    hidden under a ~440px popover width: below the `sm`
                    breakpoint the popover is `inset-x-3` (24px narrower than
                    the viewport), so a 464px viewport is a 440px popover;
                    at `sm:` and up the popover is a fixed 520px, always
                    wide enough. */}
                {monthUsage && !usageError && (
                  <GlassPill className="hidden min-[464px]:block px-3 py-1.5 text-xs text-on-surface-variant whitespace-nowrap tabular-nums">
                    {usageLabel}
                  </GlassPill>
                )}
                <Button variant="ghost" size="sm" icon="close" aria-label={t('panel.close')} onClick={closePanel} />
              </motion.div>
              <motion.div
                className="flex items-center gap-2 flex-wrap"
                initial={reduced ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0, transition: reduced ? { duration: 0 } : { ...CHROME_SPRING, delay: 0.04 } }}
              >
                <Segmented<BotTab>
                  value={activeTab}
                  onChange={setActiveTab}
                  size="xs"
                  options={[
                    { value: 'chat', icon: 'chat', label: t('tabs.chat') },
                    { value: 'memory', icon: 'psychology', label: t('tabs.memory') },
                    { value: 'report', icon: 'receipt_long', label: t('tabs.report') },
                    { value: 'settings', icon: 'settings', label: t('tabs.settings') },
                  ]}
                />
                {/* Icon-only — this IS the persisted compare-layout control
                    now (owner feedback round 3 §1: the Settings section is
                    gone). aria-pressed is always false here: this shell only
                    renders while viewMode !== 'dock' (shellVisible above), so
                    the dock state this button toggles is never active while
                    it's showing — BotCompareDock's own copy carries `true`. */}
                {canCompare && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon="open_in_full"
                    aria-label={t('dock.compare')}
                    aria-pressed={false}
                    title={t('dock.compare')}
                    disabled={patchSettings.isPending}
                    onClick={handleOpenDock}
                  />
                )}
              </motion.div>
            </div>

            {/* Body — flat data surface, DESIGN.md §14. `role="tabpanel"` +
                `aria-label` complete the tablist/tabpanel relationship the
                header's Segmented (role="tablist"/"tab") declares — an
                id/aria-labelledby pair isn't possible here since Segmented
                doesn't expose per-option ids to link to. */}
            <motion.div
              ref={bodyRef}
              className="flex-1 overflow-y-auto bg-surface-container-lowest p-4"
              initial={reduced ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0, transition: reduced ? { duration: 0 } : { ...CHROME_SPRING, delay: 0.08 } }}
              role="tabpanel"
              id={`bot-panel-tabpanel-${activeTab}`}
              aria-label={t(`tabs.${activeTab}`)}
            >
              {activeTab === 'memory' ? (
                <MemoryTab />
              ) : activeTab === 'report' ? (
                <ReportTab />
              ) : activeTab === 'settings' ? (
                <SettingsTab />
              ) : !conversation || conversation.turns.length === 0 ? (
                <EmptyState
                  icon="chat"
                  title={t('empty.title')}
                  description={t('empty.subtitle')}
                  action={
                    examples.length > 0 ? (
                      <div className="flex flex-wrap gap-2 justify-center">
                        {examples.map((example, i) => (
                          <Button key={i} variant="secondary" size="sm" onClick={() => setDraft(example)}>
                            {example}
                          </Button>
                        ))}
                      </div>
                    ) : undefined
                  }
                />
              ) : (
                <div className="space-y-5">
                  {conversation.turns.map((turn) => (
                    <TurnView key={turn.turnId} turn={turn} />
                  ))}
                </div>
              )}
            </motion.div>

            {activeTab === 'chat' && (
              <motion.div
                initial={reduced ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0, transition: reduced ? { duration: 0 } : { ...CHROME_SPRING, delay: 0.12 } }}
              >
                <BotComposer conversationId={activeConversationId} value={draft} onValueChange={setDraft} />
              </motion.div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
